import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { SpinnerGap, Plus, WarningCircle, CheckCircle } from '@phosphor-icons/react';

// ---------- Legacy validators ----------
import { EMAIL_RE } from "@/shared/emailValidation";
import { useEmailCheck, EmailHint } from "@/shared/useEmailCheck";
const PHONE_RE = /^[+0-9 ]+$/;

// Portuguese NIF checksum (matches legacy NovaLead.js::validateNIF)
function validateNifPT(value) {
  const nif = String(value || "").trim();
  if (nif.length !== 9 || !/^[0-9]+$/.test(nif)) return false;
  const starts1 = ["1", "2", "3", "5", "6", "8"];
  const starts2 = ["45", "70", "71", "72", "74", "75", "77", "79", "90", "91", "98", "99"];
  if (!starts1.includes(nif[0]) && !starts2.includes(nif.slice(0, 2))) return false;
  let total = 0;
  for (let i = 0; i < 8; i++) total += parseInt(nif[i], 10) * (9 - i);
  const mod = total % 11;
  const check = mod < 2 ? 0 : 11 - mod;
  return check === parseInt(nif[8], 10);
}

const initialLead = {
  tipoLead: "",        // id_tipo_lead (BB, BC, CS, ...)
  email: "",
  nome: "",
  telemovel: "",
  pais: "PRT",
  localidade: "",
  idEvento: "",        // Estado da Lead (ID_EstadoNegocio)
  origem: "",
  valor: 0,
  nif: "",
  referencia: "",
  descricao: "",
  observacao: "",
  dataInicio: "",
  dataPrevistaFecho: "",
  contacto: { nome: "", email: "", cargo: "", telemovel: "" },
};

export default function NewLeadPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTipo = (searchParams.get('tipoLead') || '').toUpperCase();
  const [lead, setLead] = useState({
    ...initialLead,
    tipoLead: ['BB', 'BC', 'CS'].includes(initialTipo) ? initialTipo : initialLead.tipoLead,
  });
  const [types, setTypes] = useState([]);
  const [origins, setOrigins] = useState([]);
  const [paises, setPaises] = useState([]);
  const [eventos, setEventos] = useState([]);
  const [cargos, setCargos] = useState([]);
  const [confirmAuth, setConfirmAuth] = useState(false);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [emailCheck, setEmailCheck] = useState(null);
  const [nifCheck, setNifCheck] = useState(null);          // {valid, message}
  const [empresaLookup, setEmpresaLookup] = useState(null); // {found, ownedByCurrent, ID_Empresa, NomeEmpresa, NomeConsultor}
  const [contactEmailCheck, setContactEmailCheck] = useState(null); // {valid, message}
  // Domínio do email: avisa (não bloqueia) — o consultor pode estar a
  // escrever o que o cliente ditou ao telefone.
  const emailDomainChk = useEmailCheck({ mode: "warn" });

  const isB2B = (lead.tipoLead || "").toUpperCase() === "BB";

  useEffect(() => {
    api.post('/leads/leadTypes').then(r => setTypes(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.post('/leads/typeOrigins').then(r => setOrigins(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.post('/leads/cargos').then(r => setCargos(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get('/geral/countries').then(r => {
      const list = Array.isArray(r.data) ? r.data : [];
      setPaises(list);
      // Re-assert PRT default once the list arrives (native select needs the option present)
      if (list.some(p => p.ID_Pais === 'PRT')) {
        setLead(prev => prev.pais ? prev : { ...prev, pais: 'PRT' });
      }
    }).catch(() => {});
  }, []);

  // Reload estados whenever the tipoLead changes
  useEffect(() => {
    if (!lead.tipoLead) { setEventos([]); return; }
    api.post('/leads/events', { TipoLead: lead.tipoLead })
      .then(r => setEventos(Array.isArray(r.data) ? r.data : []))
      .catch(() => setEventos([]));
    // reset state when type changes
    setLead(prev => ({ ...prev, idEvento: "" }));
  }, [lead.tipoLead]);

  const f = (key, val) => setLead(prev => ({ ...prev, [key]: val }));
  const fc = (key, val) => setLead(prev => ({ ...prev, contacto: { ...prev.contacto, [key]: val } }));

  const checkEmail = useCallback(async (email) => {
    const cleaned = (email || "").trim();
    if (!cleaned) { setEmailCheck(null); return; }
    if (!EMAIL_RE.test(cleaned)) {
      // Legacy: avisa visualmente que o email está mal formatado, em vez
      // de engolir silenciosamente (consultor pensa que está tudo OK).
      setEmailCheck({ valid: false, message: "Email num formato inválido" });
      return;
    }
    emailDomainChk.check(cleaned);
    try {
      const r = await api.post('/leads/checkEmail', { email: cleaned });
      setEmailCheck({ ...r.data, valid: true });
      // Auto-fill the form when the client belongs to this consultant's network
      // Legacy only autofills the client when tipoLead != BB
      if (r.data?.exists && r.data?.allowed && r.data?.client && !isB2B) {
        const c = r.data.client;
        setLead((prev) => ({
          ...prev,
          nome: c.NomeCompleto || prev.nome,
          telemovel: c.Telemovel || prev.telemovel,
          nif: c.Nif || prev.nif,
        }));
      }
    } catch { setEmailCheck(null); }
  }, [isB2B]);

  const checkContactEmail = (email) => {
    const cleaned = (email || "").trim();
    if (!cleaned) { setContactEmailCheck(null); return; }
    setContactEmailCheck({ valid: EMAIL_RE.test(cleaned), message: EMAIL_RE.test(cleaned) ? "Email válido" : "Email inválido" });
  };

  const verifyNif = async () => {
    const nif = (lead.nif || "").trim();
    if (lead.pais === "PRT") {
      if (nif.length !== 9 || !validateNifPT(nif)) {
        setNifCheck({ valid: false, message: "NIF inválido" });
        setEmpresaLookup(null);
        return;
      }
    } else if (nif.length <= 0) {
      setNifCheck({ valid: false, message: "NIF inválido" });
      setEmpresaLookup(null);
      return;
    }
    setNifCheck({ valid: true, message: "Válido para uso" });
    // Para B2B verificamos imediatamente se a empresa já existe noutro
    // consultor — backend rejeita à mesma, mas avisar antes do submit
    // poupa o utilizador de preencher o resto do formulário em vão.
    if (isB2B) {
      try {
        const r = await api.post('/leads/b2bEmpresaLookup', { nif });
        setEmpresaLookup(r.data || null);
      } catch (e) {
        setEmpresaLookup(null);
      }
    } else {
      setEmpresaLookup(null);
      // Lead pessoal: avisar se for um NIF de empresa (5/6/8/9) já usado
      // noutros clientes, para não poluir a base com o mesmo NIF.
      if (lead.pais === "PRT" && /^[5689]/.test(nif)) {
        try {
          const u = await api.post('/leads/nifUsage', { nif });
          if (u.data?.corporate && (u.data.count || 0) > 0) {
            const nomes = (u.data.clientes || []).map(c => c.nome).slice(0, 3).join(', ');
            setNifCheck({
              valid: true, warn: true,
              message: `Atenção: este NIF de empresa já está em ${u.data.count} cliente(s)${nomes ? ` (ex.: ${nomes})` : ''}. Confirme para não duplicar.`,
            });
          }
        } catch (e) { /* silencioso */ }
      }
    }
  };

  // Frontend submission guard (mirrors legacy disabled rules)
  const emailInvalid = !!lead.email && !EMAIL_RE.test(lead.email.trim());
  const submitDisabled =
    saving
    || !lead.tipoLead
    || !lead.nome
    || !lead.telemovel
    || !lead.pais
    || !lead.localidade
    || !lead.idEvento
    || !confirmAuth
    || !lead.email
    || emailInvalid
    || (emailCheck?.exists && emailCheck?.allowed === false)
    || (isB2B && (
      !lead.nif                          // NIF obrigatório para B2B (backend já força isto;
                                          // espelhamos no UI para evitar tentativas falhadas)
      || !lead.contacto.nome
      || !lead.contacto.telemovel
      || !lead.contacto.cargo
      || contactEmailCheck?.valid === false
      || nifCheck?.valid === false
      // Empresa pertence a outro consultor — backend rejeita; aqui
      // bloqueamos cedo e mostramos quem é o consultor "dono".
      || (empresaLookup?.found === true && empresaLookup?.ownedByCurrent === false)
    ));

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (submitDisabled) return;
    setSaving(true);
    setResult(null);
    try {
      const payload = {
        ...lead,
        valor: parseFloat(String(lead.valor).replace(',', '.')) || 0,
      };
      const r = await api.post('/leads/newFull', payload);
      setResult(r.data);
      if (r.data.success && r.data.ID_Negocio) {
        setTimeout(() => navigate(`/negocios/${r.data.ID_Negocio}`), 1500);
      }
    } catch (err) {
      setResult({ success: false, message: err.response?.data?.detail || err.response?.data?.message || 'Erro ao criar lead' });
    }
    setSaving(false);
  };

  const label = "text-xs uppercase tracking-[0.2em] font-medium text-[#6B7280] mb-1 block";
  const input = "w-full px-3 py-2 border border-[#E5E7EB] rounded-md text-sm focus:ring-2 focus:ring-[#0F1829] outline-none";

  return (
    <div data-testid="new-lead-page" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#111827]" style={{ fontFamily: "'Fraunces', sans-serif" }}>Nova Lead</h1>
          <p className="text-sm text-[#6B7280] mt-1">Página onde pode criar Novas Leads</p>
        </div>
        <button onClick={() => navigate('/leads')} className="text-sm text-[#6B7280] hover:text-[#111827]">Cancelar</button>
      </div>

      {result && (
        <div className={`mb-4 p-3 rounded-md flex items-center gap-2 text-sm ${result.success ? 'bg-green-50 border border-green-200 text-[#00C853]' : 'bg-red-50 border border-red-200 text-[#FF2B2B]'}`} data-testid="new-lead-result">
          {result.success ? <CheckCircle size={16} /> : <WarningCircle size={16} />} {result.message}
        </div>
      )}

      <form onSubmit={handleSubmit} className="bg-white border border-[#E5E7EB] rounded-lg p-6 max-w-[900px]">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* COLUNA ESQUERDA */}
          <div className="space-y-4">
            <div>
              <label className={label}>Tipo de Lead *</label>
              <select value={lead.tipoLead} onChange={e => f('tipoLead', e.target.value)} required data-testid="lead-type-select" className={input}>
                <option value="">Tipo de Lead</option>
                {types.map((t) => (
                  <option key={`tl-${t.TipoLead}`} value={t.TipoLead}>{t.Descricao || t.TipoLead}</option>
                ))}
              </select>
            </div>

            {isB2B && (
              <div>
                <label className={label}>NIF *</label>
                <input value={lead.nif} onBlur={verifyNif} onChange={e => { f('nif', e.target.value); setNifCheck(null); setEmpresaLookup(null); }} data-testid="lead-nif" placeholder="NIF" className={input} />
                {nifCheck && (
                  <p className={`text-xs mt-1 flex items-center gap-1 ${nifCheck.warn ? 'text-amber-700' : (nifCheck.valid ? 'text-green-700' : 'text-red-700')}`} data-testid="lead-nif-feedback">
                    {nifCheck.warn ? <WarningCircle size={12} weight="fill" /> : (nifCheck.valid ? <CheckCircle size={12} weight="fill" /> : <WarningCircle size={12} />)} {nifCheck.message}
                  </p>
                )}
                {empresaLookup?.found && empresaLookup.ownedByCurrent && (
                  <p className="text-xs text-emerald-700 mt-1 flex items-start gap-1" data-testid="lead-empresa-owned">
                    <CheckCircle size={12} weight="fill" className="mt-0.5 shrink-0" />
                    <span>
                      {empresaLookup.source === 'invoice'
                        ? <>NIF já consta numa morada de facturação da sua rede (encomenda #{empresaLookup.ID_Encomenda} de <span className="font-medium">{empresaLookup.NomeClienteFactura}</span>). Pode prosseguir.</>
                        : empresaLookup.source === 'client'
                          ? <>NIF já consta como cliente na sua rede: <span className="font-medium">{empresaLookup.NomeEmpresa}</span> (cliente #{empresaLookup.ID_Cliente}). Pode prosseguir.</>
                          : empresaLookup.source === 'address'
                            ? <>NIF já consta numa morada da sua rede (cliente <span className="font-medium">{empresaLookup.NomeClienteFactura}</span>). Pode prosseguir.</>
                            : <>Empresa já registada na sua rede: <span className="font-medium">{empresaLookup.NomeEmpresa}</span>. A lead vai ser ligada a esta empresa.</>}
                    </span>
                  </p>
                )}
                {empresaLookup?.found && empresaLookup.ownedByCurrent === false && (
                  <div className="mt-1 px-3 py-2 rounded-md bg-red-50 border border-red-200 text-xs text-red-800" data-testid="lead-empresa-conflict">
                    <p className="flex items-start gap-1 font-medium">
                      <WarningCircle size={14} className="mt-0.5 shrink-0" />
                      <span>
                        {empresaLookup.source === 'invoice'
                          ? 'NIF já facturou noutra rede'
                          : empresaLookup.source === 'client'
                            ? 'NIF já é cliente noutra rede'
                            : empresaLookup.source === 'address'
                              ? 'NIF já consta numa morada noutra rede'
                              : 'NIF já atribuído a outro consultor'}
                      </span>
                    </p>
                    <p className="mt-1 ml-5">
                      {empresaLookup.source === 'invoice' ? (
                        <>NIF surge na morada de facturação da encomenda #{empresaLookup.ID_Encomenda} (cliente <span className="font-medium">{empresaLookup.NomeClienteFactura}</span>), associada ao consultor <span className="font-medium">{empresaLookup.NomeConsultor}</span>. Não pode criar uma lead com este NIF noutra rede.</>
                      ) : empresaLookup.source === 'client' ? (
                        <>NIF está atribuído ao cliente <span className="font-medium">{empresaLookup.NomeEmpresa}</span> (#{empresaLookup.ID_Cliente}), associado ao consultor <span className="font-medium">{empresaLookup.NomeConsultor}</span>. Não pode criar uma lead com este NIF noutra rede.</>
                      ) : empresaLookup.source === 'address' ? (
                        <>NIF surge numa morada do cliente <span className="font-medium">{empresaLookup.NomeClienteFactura}</span> (#{empresaLookup.ID_Cliente}), associado ao consultor <span className="font-medium">{empresaLookup.NomeConsultor}</span>. Não pode criar uma lead com este NIF noutra rede.</>
                      ) : (
                        <>Empresa <span className="font-medium">{empresaLookup.NomeEmpresa}</span> está associada ao consultor <span className="font-medium">{empresaLookup.NomeConsultor}</span>. Não pode criar uma lead para esta empresa noutro consultor.</>
                      )}
                    </p>
                  </div>
                )}
              </div>
            )}

            <div>
              <label className={label}>Email *</label>
              <input type="email" required value={lead.email} onChange={e => { f('email', e.target.value); setEmailCheck(null); }} onBlur={e => checkEmail(e.target.value)} data-testid="lead-email"
                className={"w-full px-3 py-2 border rounded-md text-sm focus:ring-2 outline-none "
                  + ((emailInvalid || (emailCheck?.exists && emailCheck?.allowed === false) || emailCheck?.valid === false)
                    ? "border-red-400 focus:ring-red-500 bg-red-50"
                    : "border-[#E5E7EB] focus:ring-[#0F1829]")} />
              {emailInvalid && (
                <p className="text-xs text-red-700 mt-1 flex items-center gap-1" data-testid="lead-email-invalid-format">
                  <WarningCircle size={12} /> Email num formato inválido (ex: nome@dominio.pt)
                </p>
              )}
              <EmailHint state={emailDomainChk} testid="lead-email-hint"
                onUseSuggestion={(v) => { f('email', v); emailDomainChk.reset(); }} />
              {emailCheck?.exists && emailCheck?.allowed && !isB2B && (
                <p className="text-xs text-green-700 mt-1 flex items-center gap-1" data-testid="lead-email-autofilled">
                  <CheckCircle size={12} weight="fill" /> Cliente já na sua rede (ID: {emailCheck.client?.ID_Cliente}) — os dados foram preenchidos automaticamente.
                </p>
              )}
              {emailCheck?.exists && emailCheck?.allowed === false && (
                <p className="text-xs text-red-700 mt-1 flex items-start gap-1" data-testid="lead-email-blocked">
                  <WarningCircle size={12} className="mt-0.5 shrink-0" />
                  <span>{emailCheck.message || "Este email já está atribuído fora da sua rede e não pode ser usado."}</span>
                </p>
              )}
              {emailCheck?.exists && emailCheck?.linked_to && (
                <div className="mt-1.5 px-2.5 py-1.5 rounded-md border border-[#E5E7EB] bg-[#FAFAF8] text-xs text-[#374151] flex items-start gap-1.5"
                  data-testid="lead-email-linked-to">
                  <span className="text-[#978D78] uppercase tracking-wider text-[10px] font-bold mt-0.5 shrink-0">Ligado a</span>
                  <span className="flex-1 min-w-0">
                    <span className="font-medium text-[#0F1829]">{emailCheck.linked_to.NomeCompleto || `Cliente #${emailCheck.linked_to.ID_Cliente}`}</span>
                    {emailCheck.linked_to.ID_TipoCliente && (
                      <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-[#F5F2EB] text-[#978D78] uppercase tracking-wider">
                        {emailCheck.linked_to.ID_TipoCliente}
                      </span>
                    )}
                    {emailCheck.linked_to.Email && (
                      <span className="ml-1.5 text-[#6B7280]">· {emailCheck.linked_to.Email}</span>
                    )}
                    <span className="ml-1.5 text-[#978D78]">(ID #{emailCheck.linked_to.ID_Cliente})</span>
                  </span>
                </div>
              )}
              {emailCheck?.exists && emailCheck?.consultor &&
                emailCheck.consultor.ID_Cliente !== emailCheck?.linked_to?.ID_Cliente && (
                <div className="mt-1.5 px-2.5 py-1.5 rounded-md border border-[#E5E7EB] bg-[#FAFAF8] text-xs text-[#374151] flex items-start gap-1.5"
                  data-testid="lead-email-consultor">
                  <span className="text-[#978D78] uppercase tracking-wider text-[10px] font-bold mt-0.5 shrink-0">Consultor</span>
                  <span className="flex-1 min-w-0">
                    <span className="font-medium text-[#0F1829]">{emailCheck.consultor.NomeCompleto || `Cliente #${emailCheck.consultor.ID_Cliente}`}</span>
                    {emailCheck.consultor.Email && (
                      <span className="ml-1.5 text-[#6B7280]">· {emailCheck.consultor.Email}</span>
                    )}
                    <span className="ml-1.5 text-[#978D78]">(ID #{emailCheck.consultor.ID_Cliente})</span>
                  </span>
                </div>
              )}
              {emailCheck?.exists && !emailCheck?.linked_to && (
                <p className="text-[10px] text-[#978D78] mt-1" data-testid="lead-email-no-parent">
                  Cliente sem upline registado.
                </p>
              )}
            </div>

            <div>
              <label className={label}>Nome *</label>
              <input value={lead.nome} onChange={e => f('nome', e.target.value)} required data-testid="lead-nome" placeholder="Nome" className={input} />
            </div>

            <div>
              <label className={label}>Telemóvel *</label>
              <input value={lead.telemovel} onChange={e => f('telemovel', e.target.value)} data-testid="lead-telemovel" placeholder="Telemóvel" className={input} />
            </div>
          </div>

          {/* COLUNA DIREITA */}
          <div className="space-y-4">
            <div>
              <label className={label}>País *</label>
              <select value={lead.pais} onChange={e => f('pais', e.target.value)} onBlur={() => isB2B && verifyNif()} data-testid="lead-pais" className={input}>
                <option value="">País</option>
                {paises.map((p) => (
                  <option key={`p-${p.ID_Pais}`} value={p.ID_Pais}>{p.Descricao}</option>
                ))}
              </select>
            </div>

            <div>
              <label className={label}>Localidade *</label>
              <input value={lead.localidade} onChange={e => f('localidade', e.target.value)} data-testid="lead-localidade" placeholder="Localidade" className={input} />
            </div>

            <div>
              <label className={label}>Estado da Lead *</label>
              <select value={lead.idEvento} onChange={e => f('idEvento', e.target.value)} data-testid="lead-estado" className={input}>
                <option value="">Estado</option>
                {eventos.map((ev) => (
                  <option key={`ev-${ev.ID_EstadoNegocio}`} value={ev.ID_EstadoNegocio}>{ev.Descricao}</option>
                ))}
              </select>
            </div>

            <div>
              <label className={label}>Valor Previsto</label>
              <input type="number" step="0.01" value={lead.valor} onChange={e => f('valor', e.target.value)} data-testid="lead-valor" placeholder="Valor Previsto" className={input + " font-mono"} />
            </div>

            <div>
              <label className={label}>Origem</label>
              <select value={lead.origem} onChange={e => f('origem', e.target.value)} data-testid="lead-origem" className={input}>
                <option value="">Selecionar</option>
                {origins.map((o) => <option key={`org-${o.ID_OrigemLead}`} value={o.ID_OrigemLead}>{o.Descricao}</option>)}
              </select>
            </div>

            <div>
              <label className={label}>Observações</label>
              <textarea rows={2} value={lead.observacao} onChange={e => f('observacao', e.target.value)} data-testid="lead-observacao" placeholder="Observações" className={input + " resize-none"} />
            </div>
          </div>
        </div>

        {/* Bloco B2B - Informações Contacto Empresa */}
        {isB2B && (
          <div className="mt-6 p-4 border border-[#707070]/30 rounded-lg" data-testid="lead-b2b-contact-block">
            <p className="text-base font-semibold text-[#111827] mb-3" style={{ fontFamily: "'Fraunces', sans-serif" }}>Informações Contacto para Empresa</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={label}>Nome *</label>
                <input value={lead.contacto.nome} onChange={e => fc('nome', e.target.value)} data-testid="lead-contacto-nome" placeholder="Nome" className={input} />
              </div>
              <div>
                <label className={label}>Email *</label>
                <input type="email" value={lead.contacto.email} onChange={e => { fc('email', e.target.value); setContactEmailCheck(null); }} onBlur={e => checkContactEmail(e.target.value)} data-testid="lead-contacto-email" placeholder="Email" className={input} />
                {contactEmailCheck && (
                  <p className={`text-xs mt-1 flex items-center gap-1 ${contactEmailCheck.valid ? 'text-green-700' : 'text-red-700'}`} data-testid="lead-contacto-email-feedback">
                    {contactEmailCheck.valid ? <CheckCircle size={12} weight="fill" /> : <WarningCircle size={12} />} {contactEmailCheck.message}
                  </p>
                )}
              </div>
              <div>
                <label className={label}>Telemóvel *</label>
                <input value={lead.contacto.telemovel} onChange={e => fc('telemovel', e.target.value)} data-testid="lead-contacto-telemovel" placeholder="Telemóvel" className={input} />
              </div>
              <div>
                <label className={label}>Cargo *</label>
                <select value={lead.contacto.cargo} onChange={e => fc('cargo', e.target.value)} data-testid="lead-contacto-cargo" className={input}>
                  <option value="">Cargo</option>
                  {cargos.map((c) => (
                    <option key={`crg-${c.Id}`} value={c.Id}>{c.Descricao}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Autorização + submit */}
        <div className="mt-6 flex flex-col items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-[#111827]">
            <input type="checkbox" checked={confirmAuth} onChange={() => setConfirmAuth(v => !v)} data-testid="lead-confirm-auth" />
            Confirmo que o cliente autorizou o uso dos dados
          </label>
          <button type="submit" disabled={submitDisabled} data-testid="create-lead-btn"
            className="px-6 py-2.5 bg-[#0F1829] text-white text-sm font-medium rounded-md hover:bg-[#002480] flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
            {saving ? <SpinnerGap size={16} className="animate-spin" /> : <Plus size={16} />}
            {saving ? 'A criar Lead...' : 'Criar Lead'}
          </button>
        </div>
      </form>
    </div>
  );
}
